import type { Version, Cycle } from "@/types/types";

/**
 * Single source of truth for cycle lifecycle state.
 *
 * Pure, side-effect-free. `end_date` is INCLUSIVE, the same way every query in
 * the app reads it (`.lte('date', cycle.end_date)`) and the same way the UI
 * counts days (a 5 Sep - 19 Sep cycle is 15 days, day 15 being the 19th): the
 * cycle stays active through the whole of its last day and only becomes
 * ended-pending-debrief the morning after. So the last day is still loggable
 * and the debrief is the next day's first action, never something that
 * pre-empts the day it is meant to close.
 */
export type CycleLifecycle =
  | { state: "no-version" }
  | { state: "no-cycle"; lastEndedCycleId?: string } // gap between cycles
  | {
      state: "active";
      /** Whole days left AFTER today. 0 means today is the cycle's last day. */
      daysLeft: number;
      dayIndex: number;
      totalDays: number;
      isEndingTomorrow: boolean;
    }
  | { state: "ended-pending-debrief"; daysSinceEnd: number };

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function midnight(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).getTime();
}

export function getCycleLifecycle(
  input: { version: Version | null; cycle: Cycle | null },
  now?: Date,
): CycleLifecycle {
  const today = new Date(now ?? new Date());
  today.setHours(0, 0, 0, 0);

  if (!input.version) {
    return { state: "no-version" };
  }

  if (!input.cycle) {
    return { state: "no-cycle" };
  }

  const cycle = input.cycle;
  const cycleEnd = midnight(cycle.end_date);
  const daysLeft = Math.ceil((cycleEnd - today.getTime()) / MS_PER_DAY);
  const hasDebrief = Boolean((cycle as { debrief?: unknown }).debrief);

  // A written debrief is the closing act, so a debriefed cycle is closed on any
  // date, including its own last day (debriefing is what the user does to end
  // it; the band must not flip back to "live" underneath them).
  if (hasDebrief) {
    return { state: "no-cycle", lastEndedCycleId: cycle.id };
  }

  // >= 0, not > 0: on end_date itself daysLeft is 0 and the cycle is still
  // running (last day). It ends at that day's midnight.
  if (daysLeft >= 0) {
    const cycleStart = midnight(cycle.start_date);
    const totalDays = Math.round((cycleEnd - cycleStart) / MS_PER_DAY) + 1;
    const dayIndex = Math.min(
      totalDays,
      Math.max(1, Math.round((today.getTime() - cycleStart) / MS_PER_DAY) + 1),
    );

    return {
      state: "active",
      daysLeft,
      dayIndex,
      totalDays,
      isEndingTomorrow: daysLeft === 1,
    };
  }

  // daysLeft is negative here, so daysSinceEnd is >= 1: the day after end_date
  // is "ended 1 day ago" (yesterday), the earliest this state exists.
  return { state: "ended-pending-debrief", daysSinceEnd: -daysLeft };
}

/** 1-based day-of-cycle for a YYYY-MM-DD date, anchored at the cycle's start date. */
export function dayOfCycle(date: string, cycleStartDate: string): number {
  return Math.round((midnight(date) - midnight(cycleStartDate)) / MS_PER_DAY) + 1;
}

/** Every date in [start_date, effectiveEnd] as YYYY-MM-DD, where effectiveEnd caps
 * the cycle's end_date at today — the shared day window for report charts so all
 * sections agree on which day slots exist. */
export function cycleDateRange(startDate: string, endDate: string): string[] {
  const today = new Date();
  const todayMs = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const endMs = Math.min(midnight(endDate), todayMs);
  const dates: string[] = [];
  // Iterate with setDate (not +MS_PER_DAY) so DST-shifted days stay on local midnight.
  const d = new Date(midnight(startDate));
  while (d.getTime() <= endMs) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    dates.push(`${y}-${m}-${day}`);
    d.setDate(d.getDate() + 1);
  }
  return dates;
}
